import React, { useState, useEffect, useRef, useReducer, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Paper,
  Avatar,
  Container,
  Grid,
  IconButton,
  Drawer,
  useMediaQuery,
  useTheme,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  ListItem,
  Divider,
  ListItemIcon,
  Popover
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { AiActions } from '../../../Services/_actions/index.js';
import { useRecoilValue } from 'recoil';
import { userInformationAtom } from '../../../Services/_states/auth.js';
import MathRenderer from '../MathRenderer.tsx';
import { avatarOptions, AvatarOption } from './Avatarselector.tsx';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import Tooltip from '@mui/material/Tooltip';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import { useKnowledgeMapSubjects } from '../../Sidebar/ConceptBuilderSideBar.tsx';
import { useWebSocketAiContext } from '../../../utils/WebSocketContext.tsx';
import ConceptBuilderSidebar from './ConceptBuilderSidebar.tsx';
import TopicSubtopicCards from './TopicSubtopicCards.tsx';
import HelpDialog from '../HelpDialog.tsx';
import ScientificKeyboard from '../ScientificKeyboard.tsx';
import SchoolIcon from '@mui/icons-material/School';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import DescriptionIcon from '@mui/icons-material/Description';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import MermaidDiagram from './MermaidDiagram.tsx';
import FloatingActionBar from './FloatingActionBar.tsx';
import ImagePreview from './ImagePreview.tsx';
import ResponseNotificationSnackbar from './ResponseNotificationSnackbar.tsx';
import AnimatedVideoPlayer from './AnimatedVideoPlayer.tsx';           // ← ADD
import { VideoScene } from './AnimatedVideoPlayer.tsx';                // ← ADD
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';       // ← ADD
import { globalpermissions } from '../../../Services/Permission.js';
import { ConstantActions } from '../../../Services/_actions/constant.actions.js';
import CommonChatBubble from '../../FlowBuilder/Common/ChatMessage/Commonchatbubble.tsx';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Message {
  id: number;
  type: 'user' | 'ai';
  content: string;
  diagram_mermaid?: string;
  diagram_title?: string;
  test_url?: string;
  difficulty_buttons?: string[];
  concept_questions?: string[];
  created_at?: string;
  image_url?: string | string[];
  video_scenes?: VideoScene[];          // ← ADD
  video_title?: string;
}

// ---------------------------------------------------------------------------
// Selection state — consolidated from 4 individual useState calls
// ---------------------------------------------------------------------------

interface SelectionState {
  subject: string;
  topic: string;
  subtopic: string;
  expandedTopic: string | null;
}

type SelectionAction =
  | { type: 'SET_SUBJECT'; subject: string }
  | { type: 'SET_TOPIC'; topic: string }
  | { type: 'SET_SUBTOPIC'; subtopic: string }
  | { type: 'SET_EXPANDED_TOPIC'; topic: string | null }
  | { type: 'CLEAR_TOPIC' }
  | { type: 'CLEAR_SUBTOPIC' }
  | { type: 'RESET' };

const initialSelection: SelectionState = {
  subject: '',
  topic: '',
  subtopic: '',
  expandedTopic: null,
};

function selectionReducer(state: SelectionState, action: SelectionAction): SelectionState {
  switch (action.type) {
    case 'SET_SUBJECT':
      return { subject: action.subject, topic: '', subtopic: '', expandedTopic: null };
    case 'SET_TOPIC':
      return { ...state, topic: action.topic, subtopic: '', expandedTopic: action.topic };
    case 'SET_SUBTOPIC':
      return { ...state, subtopic: action.subtopic };
    case 'SET_EXPANDED_TOPIC':
      return { ...state, expandedTopic: action.topic };
    case 'CLEAR_TOPIC':
      return { ...state, topic: '', subtopic: '', expandedTopic: null };
    case 'CLEAR_SUBTOPIC':
      return { ...state, subtopic: '' };
    case 'RESET':
      return initialSelection;
    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Chat state — consolidated from 8 individual useState calls
// ---------------------------------------------------------------------------

interface ChatState {
  messages: Message[];
  conversationId: string | null;
  activeConversationId: string | null;
  lastQuestion: string;
  lastResponse: string;
  showActionButtons: boolean;
  isWaitingForResponse: boolean;
  messageHistory: Message[][];
}

type ChatAction =
  | { type: 'ADD_MESSAGE'; message: Message }
  | { type: 'SET_MESSAGES'; messages: Message[] }
  | { type: 'REMOVE_LAST_MESSAGE' }
  | { type: 'SET_CONVERSATION_ID'; id: string | null }
  | { type: 'SET_ACTIVE_CONVERSATION_ID'; id: string | null }
  | { type: 'SET_LAST_QUESTION'; question: string }
  | { type: 'SET_LAST_RESPONSE'; response: string }
  | { type: 'SET_SHOW_ACTION_BUTTONS'; show: boolean }
  | { type: 'SET_WAITING'; waiting: boolean }
  | { type: 'PUSH_HISTORY'; messages: Message[] }
  | { type: 'RESET' };

const initialChatState: ChatState = {
  messages: [],
  conversationId: null,
  activeConversationId: null,
  lastQuestion: '',
  lastResponse: '',
  showActionButtons: false,
  isWaitingForResponse: false,
  messageHistory: [],
};

function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'ADD_MESSAGE':
      return { ...state, messages: [...state.messages, action.message] };
    case 'SET_MESSAGES':
      return { ...state, messages: action.messages };
    case 'REMOVE_LAST_MESSAGE':
      return { ...state, messages: state.messages.slice(0, -1) };
    case 'SET_CONVERSATION_ID':
      return { ...state, conversationId: action.id };
    case 'SET_ACTIVE_CONVERSATION_ID':
      return { ...state, activeConversationId: action.id };
    case 'SET_LAST_QUESTION':
      return { ...state, lastQuestion: action.question };
    case 'SET_LAST_RESPONSE':
      return { ...state, lastResponse: action.response };
    case 'SET_SHOW_ACTION_BUTTONS':
      return { ...state, showActionButtons: action.show };
    case 'SET_WAITING':
      return { ...state, isWaitingForResponse: action.waiting };
    case 'PUSH_HISTORY':
      return { ...state, messageHistory: [...state.messageHistory, action.messages] };
    case 'RESET':
      return initialChatState;
    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Pure helpers (defined outside component — never recreated)
// ---------------------------------------------------------------------------

const extractAIText = (aiResp: any): string => {
  if (!aiResp) return 'No answer found';
  if (typeof aiResp === 'string') return aiResp.trim() || 'No answer found';
  if (aiResp?.data?.response?.agent_response) return aiResp.data.response.agent_response;
  const text =
    aiResp?.data?.response?.response ??
    aiResp?.response?.response?.response ??
    aiResp?.response?.response ??
    aiResp?.response ??
    aiResp?.output_query ??
    '';
  return typeof text === 'string' && text.trim().length > 0 ? text : 'No answer found';
};

const sanitizeImageUrl = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  const lower = trimmed.toLowerCase();
  if (lower === 'null' || lower === 'undefined') return '';
  return trimmed;
};

const getValidImageUrls = (imageUrl?: unknown): string[] => {
  if (Array.isArray(imageUrl)) {
    return imageUrl.map(sanitizeImageUrl).filter((u): u is string => Boolean(u));
  }
  const single = sanitizeImageUrl(imageUrl);
  return single ? [single] : [];
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const istDate = new Date(date.getTime() + 5.5 * 60 * 60 * 1000);
  const today = new Date(new Date().getTime() + 5.5 * 60 * 60 * 1000);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const strip = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  if (strip(istDate) === strip(today)) return 'Today';
  if (strip(istDate) === strip(yesterday)) return 'Yesterday';
  return istDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatTime = (dateString: string, addIst = false): string => {
  const date = new Date(dateString);
  const d = addIst ? new Date(date.getTime() + 5.5 * 60 * 60 * 1000) : date;
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
};

const makeMessage = (
  type: 'user' | 'ai',
  content: string,
  extras: Partial<Message> = {}
): Message => ({
  id: Date.now() + Math.random(),
  type,
  content,
  ...extras,
});

const extractVideoFromResponse = (
  aiResp: any
): { videoScenes: VideoScene[] | null; videoTitle: string } => {
  const video = aiResp?.response?.video ?? aiResp?.data?.response?.video ?? null;
  if (!video) return { videoScenes: null, videoTitle: '' };
  const scenes: VideoScene[] = Array.isArray(video.scenes) ? video.scenes : [];
  const videoTitle: string = video.video_title ?? video.title ?? '';
  return { videoScenes: scenes.length > 0 ? scenes : null, videoTitle };
};

// ---------------------------------------------------------------------------
// Memoised sub-components
// ---------------------------------------------------------------------------

interface MessageItemProps {
  msg: Message;
  onConceptClick: (q: string) => void;
  onDifficultySelect: (d: 'easy' | 'medium' | 'hard') => void;
}

// const MessageItem = React.memo(({ msg, onConceptClick, onDifficultySelect }: MessageItemProps) => {
//   const imageUrls = getValidImageUrls(msg.image_url);
//   const hasVideo = Boolean(msg.video_scenes && msg.video_scenes.length > 0);

//   // Determine if we should only show video (no text content)
//   // const shouldOnlyShowVideo = hasVideo && !msg.content?.trim();
//   const shouldOnlyShowVideo = hasVideo;

//   return (
//     <Box sx={{ mb: 2, display: 'flex', justifyContent: msg.type === 'user' ? 'flex-end' : 'flex-start' }}>
//       <Paper
//         elevation={0}
//         sx={{
//           p: shouldOnlyShowVideo ? 0 : { xs: 1.5, md: 2 }, // No padding when only video
//           borderRadius: 3,
//           bgcolor: msg.type === 'user' ? '#667eea' : '#fff',
//           color: msg.type === 'user' ? '#fff' : '#2c3e50',
//           boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
//           display: 'flex',
//           flexDirection: 'column',
//           overflowX: 'hidden',
//           wordBreak: 'break-word',
//           overflowWrap: 'anywhere',
//           whiteSpace: 'normal',
//           overflow: 'visible',
//           position: 'relative',
//           maxWidth: { xs: '85%', md: '75%' },
//           ...(shouldOnlyShowVideo && { bgcolor: 'transparent', boxShadow: 'none' }), // Transparent background when only video
//         }}
//       >
//         {msg.type === 'ai' ? (
//           <Box sx={{ display: { sx: 'inline-block', sm: 'flex' }, gap: 1 }}>
//             <Box>
//               {/* Only show text content if there's text and we're not in video-only mode */}
//               {!shouldOnlyShowVideo && msg.content && (
//                 <Typography component="div" sx={{ fontSize: { xs: 13, md: 14 }, lineHeight: 1.6 }}>
//                   <MathRenderer content={msg.content} />
//                 </Typography>
//               )}

//               {msg.diagram_mermaid && (
//                 <MermaidDiagram code={msg.diagram_mermaid} title={msg.diagram_title} />
//               )}

//               {hasVideo && (
//                 <Box sx={{ width: '100%', mt: (!shouldOnlyShowVideo && msg.content) ? 1.5 : 0 }}>
//                   <AnimatedVideoPlayer
//                     scenes={msg.video_scenes!}
//                     title={msg.video_title}
//                   />
//                 </Box>
//               )}

//               {imageUrls.length > 0 && (
//                 <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
//                   {imageUrls.map((url, i) => (
//                     <ImagePreview key={`${msg.id}-ai-${i}`} src={url} alt="Generated content" title="Generated Image" index={i} />
//                   ))}
//                 </Box>
//               )}

//               {/* Only show concept questions if not in video-only mode */}
//               {!shouldOnlyShowVideo && msg.concept_questions && (
//                 <Box sx={{ mt: 2, position: 'relative', zIndex: 10, pointerEvents: 'auto' }}>
//                   <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>🤔 You Should also Know:</Typography>
//                   <List disablePadding>
//                     {msg.concept_questions.map((q, i) => (
//                       <Box key={i} sx={{ position: 'relative', zIndex: 11 }}>
//                         <ListItemButton
//                           sx={{ px: 0, py: 1, display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', pointerEvents: 'auto', '&:hover': { backgroundColor: 'rgba(102,126,234,0.08)' } }}
//                           onClick={() => onConceptClick(q)}
//                         >
//                           <SubdirectoryArrowRightIcon sx={{ fontSize: 14 }} />
//                           <Typography sx={{ fontSize: 13 }}>{q}</Typography>
//                         </ListItemButton>
//                         {i !== (msg.concept_questions?.length ?? 0) - 1 && <Divider />}
//                       </Box>
//                     ))}
//                   </List>
//                 </Box>
//               )}

//               {/* Only show difficulty buttons if not in video-only mode */}
//               {!shouldOnlyShowVideo && msg.difficulty_buttons && (
//                 <Box sx={{ mt: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
//                   {(['easy', 'medium', 'hard'] as const).map((d) => (
//                     <Button
//                       key={d}
//                       variant="contained"
//                       size="small"
//                       onClick={() => onDifficultySelect(d)}
//                       sx={{
//                         bgcolor: d === 'easy' ? '#0077b6' : d === 'medium' ? '#fb8b24' : '#ef4444',
//                         textTransform: 'none', fontWeight: 600, fontSize: { xs: 12, md: 13 },
//                         '&:hover': { bgcolor: d === 'easy' ? '#0096c7' : d === 'medium' ? '#f6bd60' : '#dc2626' },
//                       }}
//                     >
//                       {d.charAt(0).toUpperCase() + d.slice(1)}
//                     </Button>
//                   ))}
//                 </Box>
//               )}

//               {msg.test_url && (
//                 <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e5e7eb' }}>
//                   <Typography sx={{ fontSize: { xs: 13, md: 14 }, mb: 1, fontWeight: 600 }}>📝 Test Paper Generated!</Typography>
//                   <Button variant="contained" size="small" href={msg.test_url} target="_blank" rel="noopener noreferrer"
//                     sx={{ bgcolor: '#10b981', textTransform: 'none', fontSize: { xs: 12, md: 13 }, '&:hover': { bgcolor: '#059669' } }}>
//                     Open Test Paper
//                   </Button>
//                 </Box>
//               )}

//               {msg.created_at && (
//                 <Typography variant="caption" sx={{ fontSize: 10, color: '#9e9e9e', position: 'absolute', bottom: 8, right: 12 }}>
//                   {formatTime(msg.created_at, true)}
//                 </Typography>
//               )}
//             </Box>
//           </Box>
//         ) : (
//           <>
//             <Box sx={{ display: 'flex', gap: 1, mb: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: msg.type === 'user' ? 'flex-start' : 'flex-end' }}>
//               <Typography sx={{ fontSize: { xs: 13, md: 14 }, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
//                 {typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content, null, 2)}
//               </Typography>
//               {imageUrls.length > 0 && (
//                 <Box sx={{ mt: msg.content ? 1 : 0, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
//                   {imageUrls.map((url, i) => (
//                     <ImagePreview key={`${msg.id}-user-${i}`} src={url} alt="Attached image" title="Attached Image" index={i} />
//                   ))}
//                 </Box>
//               )}
//             </Box>
//             {msg.created_at && (
//               <Typography variant="caption" sx={{ fontSize: 10, color: 'rgba(255,255,255,0.8)', position: 'absolute', bottom: 8, right: 12 }}>
//                 {formatTime(msg.created_at, true)}
//               </Typography>
//             )}
//           </>
//         )}
//       </Paper>
//     </Box>
//   );
// });

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const DoubtAssistantPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const { conversationId: urlConversationId, phoneNumber: urlPhoneNumber } = useParams<{
    conversationId?: string;
    phoneNumber?: string;
  }>();
  const { showSnackBar } = useSnackBar();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const actions = AiActions();
  const constantActions = ConstantActions();

  // -- Consolidated state --
  const [selection, dispatchSelection] = useReducer(selectionReducer, initialSelection);
  const { subject: selectedSubject, topic: selectedTopic, subtopic: selectedSubtopic, expandedTopic } = selection;

  const [chat, dispatchChat] = useReducer(chatReducer, initialChatState);
  const { messages, conversationId, activeConversationId, lastQuestion, lastResponse, showActionButtons, isWaitingForResponse } = chat;

  // -- Remaining local state --
  const [instructionSet, setInstructionSet] = useState('');
  const [userInput, setUserInput] = useState('');
  const [lastQuestionLocal, setLastQuestionLocal] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const [leadFormId, setLeadFormId] = useState('');
  const [testCampaignId, setTestCampaignId] = useState('');
  const [testLeadFormId, setTestLeadFormId] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [conversations, setConversations] = useState<any[]>([]);
  const [currentFlowType, setCurrentFlowType] = useState<'doubt' | 'test' | null>(null);
  const [pendingResponseConversationId, setPendingResponseConversationId] = useState<string | null>(null);
  const [showResponseNotification, setShowResponseNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const loggedInUser = useRecoilValue(userInformationAtom);
  const mobile = loggedInUser?.mobile;
  const userId = loggedInUser?.id || loggedInUser?.user_id || '';
  const loggedInUsername = loggedInUser?.username || '';
  const [studentClass, setStudentClass] = useState('');
  const [classGroup, setClassGroup] = useState('');
  const [userAvatar, setUserAvatar] = useState<AvatarOption>(avatarOptions[0]);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const textFieldRef = useRef<HTMLTextAreaElement | null>(null);
  const [isListening, setIsListening] = useState(false);
  const [sidebarTabValue, setSidebarTabValue] = useState(0);
  const [questionBank, setQuestionBank] = useState<any[]>([]);
  const [filteredQuestions, setFilteredQuestions] = useState<any[]>([]);
  const [instructionMode, setInstructionMode] = useState<'default' | 'solution' | 'reasoning' | 'example' | 'diagram' | 'video'>('default'); const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [awaitingDifficultySelection, setAwaitingDifficultySelection] = useState(false);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [queryHistory, setQueryHistory] = useState('');
  const hasConversationId = Boolean(urlConversationId);
  const isSelectionRequired = !hasConversationId;
  const isSuperuser = loggedInUser?.is_superuser === true || loggedInUser?.profile?.name === 'Admin';
  const [superuserSelectedClass, setSuperuserSelectedClass] = useState('');
  const hasClassListPermission = isSuperuser || constantActions.userHasPermission([globalpermissions?.performancePermissions?.classListAccess]);
  const [selectedOrganisation, setSelectedOrganisation] = useState<any>(null);

  const {
    subjects,
    loading: subjectsLoading,
    subjectHierarchy,
    availableSubjectsFromClass,
    knowledgeMapData,
    entityData: data,
  } = useKnowledgeMapSubjects(true);
  const { state } = useWebSocketAiContext();

  const subjectFromUrl = searchParams.get('subject') || '';
  const topicFromUrl = searchParams.get('topic') || '';
  const subtopicFromUrl = searchParams.get('subtopic') || '';
  const previousSubjectFromUrlRef = useRef('');

  // ---------------------------------------------------------------------------
  // FIX 1 — Build knowledgeMapData index once (O(n) total, O(1) per lookup)
  // ---------------------------------------------------------------------------

  const knowledgeMapIndex = useMemo(() => {
    const index = new Map<string, any>();
    if (!knowledgeMapData || !studentClass) return index;
    const classNum = Number(studentClass);

    // Two-pass: collect exact matches first, then ranges as fallback
    const rangeMatches = new Map<string, any>();

    for (const item of knowledgeMapData) {
      if (!item.subject || !item.class) continue;
      if (item.class.includes('-')) {
        const [start, end] = item.class.split('-').map((v: string) => Number(v.trim()));
        if (classNum >= start && classNum <= end && !rangeMatches.has(item.subject)) {
          rangeMatches.set(item.subject, item);
        }
      } else {
        if (Number(item.class) === classNum) {
          index.set(item.subject, item); // exact wins
        }
      }
    }

    // Fill in range matches only where no exact match exists
    rangeMatches.forEach((item, subject) => {
      if (!index.has(subject)) index.set(subject, item);
    });

    return index;
  }, [knowledgeMapData, studentClass]);

  // ---------------------------------------------------------------------------
  // FIX 2 — O(1) lookup helpers (replace 4 scanning functions)
  // ---------------------------------------------------------------------------

  const getKnowledgeEntry = useCallback(
    (subject: string) => knowledgeMapIndex.get(subject) ?? null,
    [knowledgeMapIndex]
  );

  const getInstructionSet = useCallback(
    (subject: string, type: 'doubt' | 'test' = 'doubt') => {
      const entry = getKnowledgeEntry(subject);
      if (!entry) return '';
      return type === 'test'
        ? entry.test_instruction_set ?? ''
        : entry.instruction_set ?? '';
    },
    [getKnowledgeEntry]
  );

  const getSolutionInstruction = useCallback(
    (subject: string) => getKnowledgeEntry(subject)?.step_by_step_solution_instruction ?? '',
    [getKnowledgeEntry]
  );

  const getReasoningInstruction = useCallback(
    (subject: string) => getKnowledgeEntry(subject)?.step_by_step_reasoning_instruction ?? '',
    [getKnowledgeEntry]
  );

  const getDiagramInstructionText = useCallback(
    (subject: string) => getKnowledgeEntry(subject)?.diagram_instruction ?? '',
    [getKnowledgeEntry]
  );

  const getVideoInstruction = useCallback(
    (subject: string) => getKnowledgeEntry(subject)?.video_instruction ?? '',
    [getKnowledgeEntry]
  );

  // ---------------------------------------------------------------------------
  // FIX 3 — Memoize the active instruction so handleApiCall reads a stable value
  // ---------------------------------------------------------------------------

  const activeInstructionSet = useMemo(() => {
    if (!selectedSubject) return '';
    switch (instructionMode) {
      case 'solution': return getSolutionInstruction(selectedSubject);
      case 'reasoning': return getReasoningInstruction(selectedSubject);
      case 'diagram': return getDiagramInstructionText(selectedSubject);
      case 'video': return getVideoInstruction(selectedSubject);
      default: return getInstructionSet(selectedSubject, 'doubt');
    }
  }, [selectedSubject, instructionMode, getSolutionInstruction, getReasoningInstruction, getDiagramInstructionText, getInstructionSet]);

  // ---------------------------------------------------------------------------
  // FIX 4 — Sync campaign IDs via O(1) lookup (replaces two .find() scans)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const entry = getKnowledgeEntry(selectedSubject);
    if (!entry) return;
    setCampaignId(entry.campaign_id ?? '');
    setLeadFormId(entry.lead_form_id ?? '');
    setTestCampaignId(entry.test_campaign_id ?? '');
    setTestLeadFormId(entry.test_lead_form_id ?? '');
  }, [selectedSubject, getKnowledgeEntry]);

  // ---------------------------------------------------------------------------
  // FIX 5 — Object URL lifecycle: create on imageFiles change, revoke on cleanup
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const urls = imageFiles.map((f) => URL.createObjectURL(f));
    setImagePreviewUrls(urls);
    return () => {
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [imageFiles]);

  // ---------------------------------------------------------------------------
  // Derived / memoised values
  // ---------------------------------------------------------------------------
  const availableClasses = useMemo(() => {
    if (!knowledgeMapData || !hasClassListPermission) return [];

    // Org filter: agar superuser ne org select ki hai toh filter karo
    const orgFilteredData = selectedOrganisation?.organisation_id
      ? knowledgeMapData.filter(
        (item: any) => item.organisation_id === selectedOrganisation.organisation_id
      )
      : knowledgeMapData;

    const classSet = new Set<string>();
    orgFilteredData.forEach((item: any) => {
      if (!item.class) return;
      const cls = String(item.class).trim();
      const normalized = cls
        .split('-')
        .map((v) => String(Number(v.trim())))
        .join('-');
      classSet.add(normalized);
    });

    return Array.from(classSet).sort((a, b) => {
      const aIsRange = a.includes('-');
      const bIsRange = b.includes('-');
      if (!aIsRange && !bIsRange) return Number(a) - Number(b);
      if (!aIsRange && bIsRange) return -1;
      if (aIsRange && !bIsRange) return 1;
      return a.localeCompare(b);
    });
  }, [knowledgeMapData, hasClassListPermission, selectedOrganisation]);
  // const subjectOptions = useMemo(
  //   () =>
  //     subjects
  //       .filter((s) => availableSubjectsFromClass.includes(s.value))
  //       .map((s) => ({ label: s.label, value: s.value })),
  //   [subjects, availableSubjectsFromClass]
  // );

  const subjectOptions = useMemo(() => {
    if (hasClassListPermission) {
      if (!superuserSelectedClass) return [];

      const normalizeClass = (cls: string) =>
        cls.split('-').map((v) => String(Number(v.trim()))).join('-');

      const selectedNormalized = normalizeClass(superuserSelectedClass);
      const validSubjects = new Set<string>();

      // Org filter apply karo
      const orgFilteredData = selectedOrganisation?.organisation_id
        ? knowledgeMapData?.filter(
          (item: any) => item.organisation_id === selectedOrganisation.organisation_id
        )
        : knowledgeMapData;

      orgFilteredData?.forEach((item: any) => {
        if (!item.subject || !item.class) return;
        const itemNormalized = normalizeClass(String(item.class).trim());
        if (itemNormalized === selectedNormalized) {
          validSubjects.add(item.subject);
          return;
        }
        if (itemNormalized.includes('-')) {
          const [start, end] = itemNormalized.split('-').map(Number);
          const selectedNum = Number(superuserSelectedClass);
          if (!isNaN(selectedNum) && selectedNum >= start && selectedNum <= end) {
            validSubjects.add(item.subject);
          }
        }
      });

      return Array.from(validSubjects).map((s) => ({ label: s, value: s }));
    }

    return subjects
      .filter((s) => availableSubjectsFromClass.includes(s.value))
      .map((s) => ({ label: s.label, value: s.value }));
  }, [subjects, availableSubjectsFromClass, hasClassListPermission, superuserSelectedClass, knowledgeMapData, selectedOrganisation]);

  const findKnowledgeEntry = useCallback(
    (subject: string) => {
      if (!knowledgeMapData || !superuserSelectedClass) return null;
      const normalizeClass = (cls: string) =>
        cls.split('-').map((v) => String(Number(v.trim()))).join('-');
      const selectedNormalized = normalizeClass(superuserSelectedClass);

      // Org filter
      const orgFilteredData = selectedOrganisation?.organisation_id
        ? knowledgeMapData.filter(
          (item: any) => item.organisation_id === selectedOrganisation.organisation_id
        )
        : knowledgeMapData;

      return orgFilteredData.find((item: any) => {
        if (!item.subject || !item.class) return false;
        if (item.subject !== subject) return false;
        const itemNormalized = normalizeClass(String(item.class).trim());
        if (itemNormalized === selectedNormalized) return true;
        if (itemNormalized.includes('-')) {
          const [start, end] = itemNormalized.split('-').map(Number);
          const num = Number(superuserSelectedClass);
          return !isNaN(num) && num >= start && num <= end;
        }
        return false;
      }) ?? null;
    },
    [knowledgeMapData, superuserSelectedClass, selectedOrganisation]
  );


  // const availableTopics = useMemo(
  //   () => (selectedSubject ? Object.keys(subjectHierarchy?.[selectedSubject] || {}) : []),
  //   [selectedSubject, subjectHierarchy]
  // );
  const availableTopics = useMemo(() => {
    if (hasClassListPermission && superuserSelectedClass && selectedSubject) {
      const entry = findKnowledgeEntry(selectedSubject);
      return (entry?.topics || []).map((t: any) => t.topic).filter(Boolean);
    }
    return selectedSubject ? Object.keys(subjectHierarchy?.[selectedSubject] || {}) : [];
  }, [hasClassListPermission, superuserSelectedClass, selectedSubject, findKnowledgeEntry, subjectHierarchy]);

  const availableSubtopics = useMemo(() => {
    if (hasClassListPermission && superuserSelectedClass && selectedSubject && selectedTopic) {
      const entry = findKnowledgeEntry(selectedSubject);
      const topicEntry = entry?.topics?.find((t: any) => t.topic === selectedTopic);
      return (topicEntry?.subtopics || []).map((s: any) => s.subtopic).filter(Boolean);
    }
    return selectedSubject && selectedTopic
      ? subjectHierarchy?.[selectedSubject]?.[selectedTopic] || []
      : [];
  }, [hasClassListPermission, superuserSelectedClass, selectedSubject, selectedTopic, findKnowledgeEntry, subjectHierarchy]);

  // FIX 4 — Campaign IDs sync (replace existing useEffect)
  useEffect(() => {
    let entry: any = null;

    if (hasClassListPermission && superuserSelectedClass) {
      // Superuser: findKnowledgeEntry use karo (class-aware)
      entry = findKnowledgeEntry(selectedSubject);
    } else {
      // Normal user: existing O(1) lookup
      entry = getKnowledgeEntry(selectedSubject);
    }

    if (!entry) {
      // Reset karo agar entry nahi mili
      setCampaignId('');
      setLeadFormId('');
      setTestCampaignId('');
      setTestLeadFormId('');
      return;
    }

    setCampaignId(entry.campaign_id ?? '');
    setLeadFormId(entry.lead_form_id ?? '');
    setTestCampaignId(entry.test_campaign_id ?? '');
    setTestLeadFormId(entry.test_lead_form_id ?? '');
  }, [selectedSubject, hasClassListPermission, superuserSelectedClass, getKnowledgeEntry, findKnowledgeEntry]);
  // const availableSubtopics = useMemo(
  //   () => (selectedSubject && selectedTopic ? subjectHierarchy?.[selectedSubject]?.[selectedTopic] || [] : []),
  //   [selectedSubject, selectedTopic, subjectHierarchy]
  // );

  const topicOptions = useMemo(
    () => availableTopics.map((t) => ({ label: t, value: t })),
    [availableTopics]
  );

  const subtopicOptions = useMemo(
    () => availableSubtopics?.map((s) => ({ label: s, value: s })),
    [availableSubtopics]
  );

  const sortedTopics = useMemo(
    () => (selectedTopic ? [selectedTopic, ...availableTopics.filter((t) => t !== selectedTopic)] : availableTopics),
    [selectedTopic, availableTopics]
  );

  const filteredQuestionsComputed = useMemo(() => {
    if (!selectedSubject || !selectedTopic || !selectedSubtopic) return [];
    const filtered = questionBank.filter(
      (item) =>
        item.entity.subject === selectedSubject &&
        item.entity.topic === selectedTopic &&
        item.entity.sub_topic === selectedSubtopic
    );
    return [...filtered].sort(() => Math.random() - 0.5).slice(0, 3);
  }, [selectedSubject, selectedTopic, selectedSubtopic, questionBank]);

  // ---------------------------------------------------------------------------
  // Stable dispatch helpers (avoid inline lambdas in JSX)
  // ---------------------------------------------------------------------------

  const addMessage = useCallback((type: 'user' | 'ai', content: string, extras: Partial<Message> = {}) => {
    dispatchChat({ type: 'ADD_MESSAGE', message: makeMessage(type, content, extras) });
  }, []);

  const resetChatState = useCallback(() => {
    dispatchChat({ type: 'RESET' });
    setUserInput('');
  }, []);

  // ---------------------------------------------------------------------------
  // URL param sync
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (subjectFromUrl) {
      dispatchSelection({ type: 'SET_SUBJECT', subject: subjectFromUrl });
      if (topicFromUrl) dispatchSelection({ type: 'SET_TOPIC', topic: topicFromUrl });
      if (subtopicFromUrl) dispatchSelection({ type: 'SET_SUBTOPIC', subtopic: subtopicFromUrl });
    }
  }, [searchParams]);

  useEffect(() => {
    const prev = previousSubjectFromUrlRef.current;
    if (prev !== subjectFromUrl) {
      if (prev || subjectFromUrl) resetChatState();
      previousSubjectFromUrlRef.current = subjectFromUrl;
    }
  }, [subjectFromUrl, resetChatState]);

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (data && loggedInUsername) handleClassGroupFromData(data);
  }, [data, loggedInUsername]);

  useEffect(() => {
    const saved = localStorage.getItem('userAvatar');
    if (!saved) return;
    try {
      const parsed = JSON.parse(saved);
      const found = avatarOptions.find((a) => a.id === parsed.id);
      if (found) setUserAvatar(found);
    } catch { /* ignore */ }
  }, []);

  const handleClassGroupFromData = (entityData: any) => {
    if (!entityData?.items?.length || !loggedInUsername) return;
    entityData.items.forEach((item: any) => {
      if (item?.entity?.username?.toLowerCase() === loggedInUsername.toLowerCase()) {
        setStudentClass(item.entity.class);
        setClassGroup(item.entity.class_group);
      }
    });
  };

  const fetchQuestionBank = useCallback(async () => {
    try {
      const response = await actions.getRecordEntity('', { template_form_type: 'question_bank_form' });
      if (response?.items) setQuestionBank(response.items);
    } catch (e) {
      console.error('Error fetching question bank:', e);
    }
  }, []);

  useEffect(() => {
    fetchQuestionBank();
  }, [fetchQuestionBank]);

  // ---------------------------------------------------------------------------
  // Conversation list
  // ---------------------------------------------------------------------------

  const fetchConversationList = useCallback(
    async (forceNoFilter = false) => {
      try {
        const payload = {
          user_id: userId,
          phone_number: mobile ? `91${mobile}` : '',
          ...(!forceNoFilter && sidebarTabValue === 1 && (selectedSubject || selectedTopic)
            ? { parameters: { ...(selectedSubject && { subject: selectedSubject }), ...(selectedTopic && { topic: selectedTopic }) } }
            : {}),
        };
        const response = await actions.getConversationList(payload);
        if (response?.status && Array.isArray(response.data)) {
          const list = response.data.map((c: any) => ({ ...c, last_query: c.last_query || c.query || 'No query' }));
          setConversations(list);
          applyRouteConversationContext(list, urlConversationId);
        }
      } catch (e) {
        console.error('Failed to fetch conversations:', e);
      }
    },
    [userId, mobile, selectedSubject, selectedTopic, sidebarTabValue, urlConversationId]
  );

  useEffect(() => {
    if (userId) fetchConversationList(sidebarTabValue === 0);
  }, [selectedSubject, selectedTopic, userId, sidebarTabValue]);

  const applyRouteConversationContext = (list: any[], routeConversationId?: string) => {
    if (!routeConversationId || !Array.isArray(list)) return;
    const matched = list.find((c) => c.conversation_id === routeConversationId);
    if (!matched?.parameters) return;
    const { subject = '', topic = '', subtopic = '' } = matched.parameters;
    if (subject) dispatchSelection({ type: 'SET_SUBJECT', subject });
    if (topic) dispatchSelection({ type: 'SET_TOPIC', topic });
    if (subtopic) dispatchSelection({ type: 'SET_SUBTOPIC', subtopic });
  };

  // ---------------------------------------------------------------------------
  // Fetch chat history
  // ---------------------------------------------------------------------------

  const fetchQuery = useCallback(async (cid: string) => {
    try {
      const response = await actions.getChatHistory({ conversation_id: cid, ai_model: 'mdai' });
      if (!response?.status || !Array.isArray(response.data)) return;

      const chatMessages: Message[] = response.data.flatMap((item: any) => {
        const msgs: Message[] = [];

        if (item.query) {
          msgs.push(
            makeMessage('user', item.query, {
              created_at: item.created_at,
              image_url: item?.image_urls || '',
            })
          );
        }

        const aiText = extractAIText(item?.response);
        const conceptQuestions = item?.response?.response?.concept_check_questions;
        const imageUrl = item?.response?.data?.response?.image_url || '';
        const diagramMermaid = item?.response?.response?.diagrams?.mermaid_code || '';
        const diagramTitle = item?.response?.response?.diagrams?.diagram_title || '';

        // ✅ Extract video exactly like diagram — from response.video
        const { videoScenes: vs, videoTitle: vt } = extractVideoFromResponse(item?.response);

        msgs.push(
          makeMessage('ai', aiText, {
            diagram_mermaid: diagramMermaid,
            diagram_title: diagramTitle,
            created_at: item.created_at,
            ...(imageUrl && { image_url: imageUrl }),
            ...(Array.isArray(conceptQuestions) && conceptQuestions.length > 0
              ? { concept_questions: conceptQuestions }
              : {}),
            ...(vs ? { video_scenes: vs, video_title: vt } : {}),
          })
        );

        return msgs;
      });

      dispatchChat({ type: 'SET_MESSAGES', messages: chatMessages });
      dispatchChat({ type: 'SET_CONVERSATION_ID', id: cid });
      dispatchChat({ type: 'SET_ACTIVE_CONVERSATION_ID', id: cid });
      dispatchChat({ type: 'SET_SHOW_ACTION_BUTTONS', show: true });

      const lastItem = response.data[response.data.length - 1];
      if (lastItem) dispatchChat({ type: 'SET_LAST_QUESTION', question: lastItem.query || '' });

      const apiParams = response.data[0]?.parameters;
      const responseData = response.data[0]?.response?.response;
      const subject = apiParams?.subject || responseData?.subject || '';
      const topic = apiParams?.topic || responseData?.topic || '';
      const subtopic = apiParams?.subtopic || responseData?.subtopic || '';
      const queryHistory = lastItem?.query || apiParams?.query || lastItem?.response?.response?.query || '';

      setQueryHistory(queryHistory);
      if (subject) dispatchSelection({ type: 'SET_SUBJECT', subject });
      if (topic) dispatchSelection({ type: 'SET_TOPIC', topic });
      if (subtopic) dispatchSelection({ type: 'SET_SUBTOPIC', subtopic });

      if (isMobile) setSidebarOpen(false);
    } catch (e) {
      console.error('Failed to fetch conversation:', e);
    }
  }, [isMobile]);

  // Shared conversation from URL
  useEffect(() => {
    if (!urlConversationId || !urlPhoneNumber) return;
    const isUserId = urlPhoneNumber.length < 12;
    if (isUserId) {
      if (String(urlPhoneNumber) !== String(userId)) {
        showSnackBar('This conversation belongs to a different user.', 'error');
        navigate('/r/student/concept-builder');
        return;
      }
    } else {
      const clean = (p: string) => (p.startsWith('91') ? p.substring(2) : p);
      if (clean(urlPhoneNumber) !== clean(mobile || '')) {
        showSnackBar('This conversation belongs to a different user.', 'error');
        navigate('/r/student/concept-builder');
        return;
      }
    }
    fetchQuery(urlConversationId)
      .then(() => showSnackBar('Conversation loaded successfully!', 'success'))
      .catch(() => {
        showSnackBar('Failed to load the conversation.', 'error');
        navigate('/r/student/concept-builder');
      });
  }, [urlConversationId, urlPhoneNumber]);

  // ---------------------------------------------------------------------------
  // Scroll to bottom
  // ---------------------------------------------------------------------------

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ---------------------------------------------------------------------------
  // WebSocket response handler
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!state?.imageGeneration) return;
    const imageUrl = state.imageGeneration.image_url;
    if (!imageUrl) return;
    dispatchChat({ type: 'REMOVE_LAST_MESSAGE' });
    dispatchChat({
      type: 'ADD_MESSAGE',
      message: makeMessage('ai', '🖼️ Here is your generated image:', { image_url: imageUrl }),
    });
  }, [state?.imageGeneration]);

  useEffect(() => {
    if (!state?.aiRes) return;

    const aiResp = state.aiRes.ai_response;
    const responseConversationId = state.aiRes.conversation_id;
    const isDifferentChat =
      responseConversationId &&
      activeConversationId &&
      responseConversationId !== activeConversationId;

    if (isDifferentChat) {
      const aiText =
        typeof aiResp === 'string'
          ? aiResp
          : aiResp?.response?.response || aiResp?.output_query || 'Response received';
      setNotificationMessage(aiText);
      setShowResponseNotification(true);
      setPendingResponseConversationId(responseConversationId);
      dispatchChat({ type: 'SET_WAITING', waiting: false });
      return;
    }

    dispatchChat({ type: 'REMOVE_LAST_MESSAGE' });

    if (currentFlowType === 'test') {
      const testUrl = aiResp?.response?.test_url;
      const content = aiResp?.response?.response || '📝 Test Paper Generated!';
      dispatchChat({
        type: 'ADD_MESSAGE',
        message: makeMessage('ai', content, testUrl ? { test_url: testUrl } : {}),
      });
    } else {
      const aiText = typeof aiResp === 'string'
        ? aiResp
        : aiResp?.response?.response || aiResp?.output_query || 'No answer found';

      const conceptQuestions =
        aiResp?.response?.response?.concept_check_questions ||
        aiResp?.response?.concept_check_questions;

      const diagramMermaid = aiResp?.response?.diagrams?.mermaid_code || '';
      const diagramTitle = aiResp?.response?.diagrams?.diagram_title || '';

      // ✅ Extract video exactly like diagram
      const { videoScenes, videoTitle } = extractVideoFromResponse(aiResp);

      dispatchChat({ type: 'SET_LAST_RESPONSE', response: String(aiText) });
      dispatchChat({
        type: 'ADD_MESSAGE',
        message: makeMessage('ai', String(aiText), {
          diagram_mermaid: diagramMermaid,
          diagram_title: diagramTitle,
          ...(Array.isArray(conceptQuestions) && conceptQuestions.length > 0
            ? { concept_questions: conceptQuestions }
            : {}),
          ...(videoScenes ? { video_scenes: videoScenes, video_title: videoTitle } : {}),
        }),
      });
    }

    if (responseConversationId) {
      dispatchChat({ type: 'SET_CONVERSATION_ID', id: responseConversationId });
      dispatchChat({ type: 'SET_ACTIVE_CONVERSATION_ID', id: responseConversationId });
      fetchConversationList();
      setConversations((prev) => {
        const exists = prev.some((c) => c.conversation_id === responseConversationId);
        if (exists)
          return prev.map((c) =>
            c.conversation_id === responseConversationId
              ? { ...c, query: lastQuestion, last_query: lastQuestion }
              : c
          );
        return [
          { conversation_id: responseConversationId, query: lastQuestion, last_query: lastQuestion },
          ...prev,
        ];
      });
    }

    dispatchChat({ type: 'SET_WAITING', waiting: false });
    dispatchChat({ type: 'SET_SHOW_ACTION_BUTTONS', show: true });
    setCurrentFlowType(null);
    setAwaitingDifficultySelection(false);
    setPendingResponseConversationId(null);
  }, [state?.aiRes]);


  // ---------------------------------------------------------------------------
  // API call
  // ---------------------------------------------------------------------------

  const handleApiCall = useCallback(async (
    type: 'doubt' | 'test',
    difficulty?: 'easy' | 'medium' | 'hard',
    bypassSelectionCheck = false
  ) => {
    if (type === 'doubt' && !userInput.trim() && imageFiles.length === 0) return;
    if (!bypassSelectionCheck && isSelectionRequired && (!selectedSubject || !selectedTopic || !selectedSubtopic)) {
      showSnackBar('Please select Subject, Topic and Subtopic before asking a question.', 'warning');
      return;
    }

    dispatchChat({ type: 'PUSH_HISTORY', messages });
    setCurrentFlowType(type);
    dispatchChat({ type: 'SET_SHOW_ACTION_BUTTONS', show: false });
    dispatchChat({ type: 'SET_WAITING', waiting: true });

    const queryConversationId = conversationId;
    setPendingResponseConversationId(queryConversationId);

    if (type === 'doubt') {
      if (imageFiles.length > 0) {
        dispatchChat({
          type: 'ADD_MESSAGE',
          message: makeMessage('user', userInput || '', { image_url: imagePreviewUrls }),
        });
      } else {
        addMessage('user', userInput);
      }
      dispatchChat({ type: 'SET_LAST_QUESTION', question: userInput });
      setLastQuestionLocal(userInput);
      setUserInput('');
      addMessage('ai', `Let me think for a moment… 🤔\nI'm carefully preparing something special for you.\nThis may take up to a minute.`);
    } else {
      addMessage('user', `Selected: ${difficulty?.charAt(0).toUpperCase()}${difficulty?.slice(1)}`);
      addMessage('ai', 'Generating test paper... 📝');
    }

    try {
      const instructionParts = [
        `You are teaching a student of Class ${studentClass}.`,
        `Answer strictly according to this class level.`,
        ...(selectedSubject ? [`Subject: ${selectedSubject}.`] : []),
        ...(selectedTopic ? [`Topic: ${selectedTopic}.`] : []),
        ...(selectedSubtopic ? [`Subtopic: ${selectedSubtopic}.`] : []),
      ];

      // FIX 3 applied — use pre-computed activeInstructionSet for doubt, O(1) for test
      if (type === 'doubt') {
        if (instructionMode === 'solution') instructionParts.push('Provide a detailed step-by-step solution.');
        else if (instructionMode === 'reasoning') instructionParts.push('Explain the step-by-step reasoning process.');
        else if (instructionMode === 'diagram') {
          instructionParts.push('Explain using a diagram or visual representation.');
          instructionParts.push('Explain the step-by-step reasoning process.');
        }
        instructionParts.push(activeInstructionSet);
      } else {
        instructionParts.push(getInstructionSet(selectedSubject, 'test'));
      }

      const buildFormData = (overrides: Record<string, string> = {}) => {
        const fd = new FormData();
        fd.append('flowType', type);
        fd.append('class', studentClass);
        fd.append('parameters', JSON.stringify({ subject: selectedSubject, topic: selectedTopic, subtopic: selectedSubtopic }));
        fd.append('instruction', instructionParts.join('\n'));
        if (queryConversationId) fd.append('conversation_id', queryConversationId);
        Object.entries(overrides).forEach(([k, v]) => fd.append(k, v));
        return fd;
      };

      if (type === 'doubt') {
        const fd = buildFormData({
          query: userInput || '',
          campaign_id: campaignId || '',
          lead_form_id: leadFormId || '',
        });
        imageFiles.forEach((file) => fd.append('images', file));
        setImageFiles([]);
        if (fileInputRef.current) fileInputRef.current.value = '';
        await actions.sendDoubtWithImages(fd);
      } else {
        const query = `${lastQuestionLocal ? `Generate test for "${lastQuestionLocal}"` : 'Generate test paper'} ${selectedSubject} ${selectedTopic} ${selectedSubtopic || ''} test level ${difficulty} for class ${studentClass}`;
        const fd = buildFormData({
          query,
          campaign_id: testCampaignId || '',
          lead_form_id: testLeadFormId || '',
          level: difficulty || '',
        });
        fd.delete('parameters'); // remove the one buildFormData added
        fd.append('parameters', JSON.stringify({
          subject: selectedSubject,
          topic: selectedTopic,
          subtopic: selectedSubtopic,
          difficulty
        }));
        // await actions.getTestDoubtData(fd);
        await actions.sendDoubtWithImages(fd);
      }
    } catch (e) {
      dispatchChat({ type: 'REMOVE_LAST_MESSAGE' });
      addMessage('ai', '❌ Sorry, something went wrong. Please try again.');
      dispatchChat({ type: 'SET_WAITING', waiting: false });
      dispatchChat({ type: 'SET_SHOW_ACTION_BUTTONS', show: true });
      setAwaitingDifficultySelection(false);
      setPendingResponseConversationId(null);
      console.error(e);
    }
  }, [userInput, imageFiles, imagePreviewUrls, isSelectionRequired, selectedSubject, selectedTopic, selectedSubtopic, messages, conversationId, studentClass, instructionMode, activeInstructionSet, campaignId, leadFormId, testCampaignId, testLeadFormId, lastQuestionLocal, getInstructionSet, addMessage]);

  // ---------------------------------------------------------------------------
  // Image upload
  // ---------------------------------------------------------------------------

  const handleImageUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setImageFiles((prev) => [...prev, ...files]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleClearImage = useCallback(() => {
    setImageFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleRemoveImage = useCallback((index: number) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // ---------------------------------------------------------------------------
  // Mic / speech
  // ---------------------------------------------------------------------------

  const stopMic = useCallback(() => {
    recognitionRef.current?.stop();
    recognitionRef.current = null;
    setIsListening(false);
  }, []);

  const handleMicClick = useCallback(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert('Speech recognition not supported in this browser.');
      return;
    }
    if (isListening) { stopMic(); return; }
    setUserInput('');
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    recognitionRef.current = rec;
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-US';
    rec.onresult = (event: SpeechRecognitionEvent) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) setUserInput((p) => p + event.results[i][0].transcript + ' ');
      }
    };
    rec.onend = () => setIsListening(false);
    rec.onerror = () => setIsListening(false);
    rec.start();
    setIsListening(true);
  }, [isListening, stopMic]);

  // ---------------------------------------------------------------------------
  // Navigation / selection handlers
  // ---------------------------------------------------------------------------

  const handleSubjectChange = useCallback((subject: string) => {
    dispatchSelection({ type: 'SET_SUBJECT', subject });
    resetChatState();
    navigate(`/r/student/concept-builder?subject=${subject}`, { replace: true });
  }, [navigate, resetChatState]);

  const handleTopicChange = useCallback((topic: string) => {
    dispatchSelection({ type: 'SET_TOPIC', topic });
    resetChatState();
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      topic ? p.set('topic', topic) : p.delete('topic');
      p.delete('subtopic');
      return p;
    });
  }, [resetChatState, setSearchParams]);

  const handleTopicClick = useCallback((topic: string) => {
    dispatchSelection({ type: 'SET_TOPIC', topic });
    resetChatState();
  }, [resetChatState]);

  const handleSubtopicChange = useCallback((subtopic: string) => {
    dispatchSelection({ type: 'SET_SUBTOPIC', subtopic });
    resetChatState();
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      subtopic ? p.set('subtopic', subtopic) : p.delete('subtopic');
      return p;
    });
  }, [resetChatState, setSearchParams]);

  const handleEditSubject = useCallback(() => {
    dispatchSelection({ type: 'RESET' });
    resetChatState();
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete('subject'); p.delete('topic'); p.delete('subtopic');
      return p;
    });
  }, [resetChatState, setSearchParams]);

  const handleEditTopic = useCallback(() => {
    dispatchSelection({ type: 'CLEAR_TOPIC' });
    resetChatState();
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete('topic'); p.delete('subtopic');
      return p;
    });
  }, [resetChatState, setSearchParams]);

  const handleEditSubtopic = useCallback(() => {
    dispatchSelection({ type: 'CLEAR_SUBTOPIC' });
    resetChatState();
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete('subtopic');
      return p;
    });
  }, [resetChatState, setSearchParams]);

  const handleNewQuery = useCallback((subtopic?: string) => {
    const params = new URLSearchParams();
    if (selectedSubject) params.set('subject', selectedSubject);
    navigate(`/r/student/concept-builder?${params.toString()}`, { replace: true });
    dispatchChat({ type: 'RESET' });
    setUserInput('');
    setAwaitingDifficultySelection(false);
    fetchConversationList();
  }, [selectedSubject, navigate, fetchConversationList]);

  const handleNewChatClick = useCallback((topic: string) => {
    dispatchSelection({ type: 'SET_TOPIC', topic });
    resetChatState();
  }, [resetChatState]);

  const handleSidebarTabChange = useCallback((value: number) => {
    setSidebarTabValue(value);
    dispatchSelection({ type: 'SET_EXPANDED_TOPIC', topic: null });
    dispatchChat({ type: 'SET_ACTIVE_CONVERSATION_ID', id: null });
  }, []);

  const handleConceptQuestionClick = useCallback((question: string) => {
    setUserInput(question);
    setTimeout(() => handleApiCall('doubt', undefined, true), 0);
  }, [handleApiCall]);

  const handleSendQuestion = useCallback(() => {
    stopMic();
    dispatchChat({ type: 'SET_SHOW_ACTION_BUTTONS', show: false });
    handleApiCall('doubt');
  }, [stopMic, handleApiCall]);

  const handleDifficultySelection = useCallback((difficulty: 'easy' | 'medium' | 'hard') => {
    stopMic();
    setAwaitingDifficultySelection(false);
    handleApiCall('test', difficulty);
  }, [stopMic, handleApiCall]);

  const handleGetTestPaper = useCallback(() => {
    addMessage('user', 'Practice Test');
    addMessage('ai', 'Please select the difficulty level for your test:', { difficulty_buttons: ['easy', 'medium', 'hard'] } as any);
    dispatchChat({ type: 'SET_SHOW_ACTION_BUTTONS', show: false });
    setAwaitingDifficultySelection(true);
  }, [addMessage]);

  const handleGetImage = useCallback(async () => {
    if (!lastQuestionLocal.trim() && !queryHistory.trim()) {
      showSnackBar('Please ask a question first before requesting an image.', 'warning');
      return;
    }
    addMessage('ai', `🎨 Generating your image...\nThis may take a moment. Please wait.`);
    try {
      const instructionParts = [
        `You are teaching a student of Class ${studentClass}.`,
        `Answer strictly according to this class level.`,
        ...(selectedSubject ? [`Subject: ${selectedSubject}.`] : []),
        ...(selectedTopic ? [`Topic: ${selectedTopic}.`] : []),
        ...(selectedSubtopic ? [`Subtopic: ${selectedSubtopic}.`] : []),
        activeInstructionSet,
      ];
      const payload = {
        query: lastQuestionLocal || queryHistory,
        instruction: instructionParts.join('\n'),
        flowType: 'doubt',
        campaign_id: campaignId,
        lead_form_id: leadFormId,
        parameters: { subject: selectedSubject, topic: selectedTopic, subtopic: selectedSubtopic },
        class: studentClass,
        ...(conversationId && { conversation_id: conversationId }),
      };
      const response = await actions.getImageUrl(payload);
      if (!response?.status) {
        dispatchChat({ type: 'REMOVE_LAST_MESSAGE' });
        addMessage('ai', '❌ Failed to generate image. Please try again.');
      }
    } catch {
      dispatchChat({ type: 'REMOVE_LAST_MESSAGE' });
      addMessage('ai', '❌ Failed to generate image. Please try again.');
      showSnackBar('Failed to generate image. Please try again.', 'error');
    }
  }, [lastQuestionLocal, studentClass, queryHistory, selectedSubject, selectedTopic, selectedSubtopic, activeInstructionSet, campaignId, leadFormId, conversationId, addMessage, showSnackBar]);

  const handleNavigateToResponse = useCallback(async () => {
    if (!pendingResponseConversationId) return;
    setShowResponseNotification(false);
    await fetchConversationList();
    await fetchQuery(pendingResponseConversationId);
    if (isMobile) setSidebarOpen(false);
  }, [pendingResponseConversationId, fetchConversationList, fetchQuery, isMobile]);

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    let text = e.clipboardData.getData('text/html');
    if (text) {
      const div = document.createElement('div');
      div.innerHTML = text;
      text = div.textContent || div.innerText || '';
    } else {
      text = e.clipboardData.getData('text/plain');
    }
    if (!text) return;
    const textarea = textFieldRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const next = userInput.substring(0, start) + text + userInput.substring(end);
      setUserInput(next);
      requestAnimationFrame(() => {
        if (!textFieldRef.current) return;
        const pos = start + text.length;
        textFieldRef.current.selectionStart = pos;
        textFieldRef.current.selectionEnd = pos;
        textFieldRef.current.focus();
      });
    } else {
      setUserInput((p) => p + text);
    }
  }, [userInput]);

  const handleInsertSymbol = useCallback((symbol: string) => {
    setUserInput((p) => p + symbol);
  }, []);

  const handlePredefinedQuestionClick = useCallback((questionData: any, type: 'doubt' | 'test') => {
    const questionText = questionData?.entity?.question || questionData?.question || questionData;
    if (type === 'doubt') {
      addMessage('user', questionText);
      const entity = questionData?.entity;
      if (entity) {
        let aiResponse = '';
        if (entity.definition) aiResponse += `**Definition:**\n${entity.definition}\n\n`;
        if (entity.key_theorem) aiResponse += `**Key Concept:**\n${entity.key_theorem.replace(/\\n\\n/g, '\n\n').replace(/\\n/g, '\n')}\n\n`;
        if (entity.correct_explanation) aiResponse += `**Explanation:**\n${entity.correct_explanation}\n\n`;
        if (entity.real_life_examples) aiResponse += `**Real Life Examples:**\n${entity.real_life_examples}\n\n`;
        if (entity.common_misunderstanding) aiResponse += `**Common Misunderstandings:**\n${entity.common_misunderstanding.replace(/\\n•/g, '\n• ').replace(/\\n-/g, '\n- ')}\n\n`;
        if (entity.sources) aiResponse += `**Sources:**\n${entity.sources.replace(/🔹/g, '• ').replace(/\\n-/g, '\n- ').replace(/- https/g, '• https')}`;
        let conceptQuestions: string[] = [];
        if (typeof entity.concept_check_questions === 'string') {
          conceptQuestions = entity.concept_check_questions.split(/[,\n]/).map((q: string) => q.trim()).filter(Boolean);
        } else if (Array.isArray(entity.concept_check_questions)) {
          conceptQuestions = entity.concept_check_questions;
        }
        dispatchChat({
          type: 'ADD_MESSAGE',
          message: makeMessage('ai', aiResponse, conceptQuestions.length > 0 ? { concept_questions: conceptQuestions } : {}),
        });
        dispatchChat({ type: 'SET_SHOW_ACTION_BUTTONS', show: true });
      } else {
        setUserInput(questionText);
        setTimeout(() => handleApiCall('doubt', undefined, true), 100);
      }
    } else {
      addMessage('user', questionText);
      addMessage('ai', 'Please select the difficulty level for your test:');
      dispatchChat({ type: 'SET_SHOW_ACTION_BUTTONS', show: false });
    }
  }, [addMessage, handleApiCall]);

  // ---------------------------------------------------------------------------
  // Sidebar props (stable object via useMemo)
  // ---------------------------------------------------------------------------

  const sidebarProps = useMemo(() => ({
    isMobile,
    selectedSubject,
    selectedTopic,
    selectedSubtopic,
    subjectOptions,
    topicOptions,
    subtopicOptions,
    availableTopics,
    sortedTopics,
    conversations,
    activeConversationId,
    expandedTopic,
    onClose: () => setSidebarOpen(false),
    onSubjectChange: handleSubjectChange,
    onTopicChange: handleTopicChange,
    onSubtopicChange: handleSubtopicChange,
    onTopicClick: handleTopicClick,
    onExpandTopic: (t: string | null) => dispatchSelection({ type: 'SET_EXPANDED_TOPIC', topic: t }),
    onNewChatClick: handleNewChatClick,
    onConversationClick: fetchQuery,
    onTabChange: handleSidebarTabChange,
    isSuperuser: hasClassListPermission,
    availableClasses,
    superuserSelectedClass,
    onSuperuserClassChange: (cls: string) => {
      setSuperuserSelectedClass(cls);
      setStudentClass(cls);
      dispatchSelection({ type: 'RESET' });
      resetChatState();
    },
    selectedOrganisation,
    onOrganisationChange: (org: any) => {
      setSelectedOrganisation(org);
      setSuperuserSelectedClass('');
      setStudentClass('');
      dispatchSelection({ type: 'RESET' });
      resetChatState();
    },
  }), [isMobile, selectedSubject, selectedTopic, selectedSubtopic, subjectOptions, topicOptions, subtopicOptions, availableTopics, sortedTopics, conversations, activeConversationId, expandedTopic, handleSubjectChange, handleTopicChange, handleSubtopicChange, handleTopicClick, handleNewChatClick, fetchQuery, handleSidebarTabChange, isSuperuser, availableClasses, superuserSelectedClass,
    selectedOrganisation,
    resetChatState,]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------


  return (
    <Container
      maxWidth="xl"
      className="message-outerwrapper"
      sx={{ py: 0.5, height: { xs: 'calc(100vh - 0px)', sm: 'calc(100vh - 0px)' }, display: 'flex', flexDirection: 'column', px: { xs: 1, md: 'auto' }, maxWidth: '100% !important' }}
    >
      <Grid container spacing={0.25} sx={{ flex: 1, overflow: 'hidden' }}>
        {isMobile && (
          <Drawer anchor="left" open={sidebarOpen} onClose={() => setSidebarOpen(false)} ModalProps={{ keepMounted: true }} sx={{ '& .MuiDrawer-paper': { width: '85%', maxWidth: 360 } }}>
            <ConceptBuilderSidebar {...sidebarProps} />
          </Drawer>
        )}
        {!isMobile && (
          <Grid item md={3} sx={{ height: '100%' }}>
            <Paper elevation={2} sx={{ borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <ConceptBuilderSidebar {...sidebarProps} />
            </Paper>
          </Grid>
        )}

        <Grid item xs={12} md={isMobile ? 12 : 9} sx={{ height: '100%', display: 'flex' }}>
          <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden', width: '100%', display: 'flex', flexDirection: 'column', mb: '5px', position: 'relative' }}>

            {/* Header */}
            <Box sx={{ p: 1.3, bgcolor: 'primary.main', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { md: 1.5, xs: 1 }, paddingLeft: isMobile ? '42px' : '0' }}>
                {isMobile && (
                  <IconButton onClick={() => setSidebarOpen(true)} sx={{ color: '#fff', mr: 0.5 }}>
                    <MenuIcon />
                  </IconButton>
                )}
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: { xs: 32, md: 40 }, height: { xs: 32, md: 40 } }}>
                  <SmartToyIcon sx={{ fontSize: { xs: 18, md: 24 } }} />
                </Avatar>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1rem', md: '1.25rem' }, lineHeight: 1.2 }}>
                      Concept Builder
                    </Typography>
                    {studentClass && (
                      <Typography sx={{ fontSize: { xs: 10, md: 11 }, color: '#fff', px: 1, py: 0.2, borderRadius: 1 }}>
                        Class {studentClass}
                      </Typography>
                    )}
                  </Box>
                  {selectedSubject && (
                    <Typography sx={{ fontSize: { xs: 10, md: 12 }, opacity: 0.9, display: 'block', mt: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: { xs: 160, md: 300 } }}>
                      {selectedSubject}
                      {selectedTopic && <><span>→</span><span>{selectedTopic}</span></>}
                      {selectedSubtopic && <><span>→</span><span>{selectedSubtopic}</span></>}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Tooltip title="How to use Concept Builder" arrow>
                <IconButton onClick={() => setHelpDialogOpen(true)} sx={{ color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
                  <InfoOutlinedIcon />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Chat area */}
            <Box
              sx={{
                flex: 1, overflowY: 'auto', p: { xs: 2, md: 3 }, bgcolor: '#f0f2f5',
                backgroundImage: `radial-gradient(circle at 20px 20px, rgba(0,0,0,0.02) 1px, transparent 1px), radial-gradient(circle at 80px 80px, rgba(0,0,0,0.02) 1px, transparent 1px)`,
                backgroundSize: '100px 100px',
                display: 'flex', flexDirection: 'column',
                justifyContent: messages.length === 0 ? 'center' : 'flex-start',
                alignItems: messages.length === 0 ? 'center' : 'stretch',
              }}
            >
              {messages.length === 0 ? (
                <TopicSubtopicCards
                  selectedSubject={selectedSubject}
                  selectedTopic={selectedTopic}
                  selectedSubtopic={selectedSubtopic}
                  availableSubjects={subjects.map((s) => s.value)}
                  availableTopics={availableTopics}
                  availableSubtopics={availableSubtopics}
                  onSubjectSelect={handleSubjectChange}
                  onTopicSelect={(topic) => { dispatchSelection({ type: 'SET_TOPIC', topic }); }}
                  onSubtopicSelect={(subtopic) => { dispatchSelection({ type: 'SET_SUBTOPIC', subtopic }); }}
                  filteredQuestions={filteredQuestionsComputed}
                  onPredefinedQuestionClick={handlePredefinedQuestionClick}
                  onEditSubject={handleEditSubject}
                  onEditTopic={handleEditTopic}
                  onEditSubtopic={handleEditSubtopic}
                />
              ) : (
                <>
                  {messages.map((msg, index) => {
                    const showDateSep =
                      index === 0 ||
                      (msg.created_at && messages[index - 1]?.created_at && formatDate(msg.created_at) !== formatDate(messages[index - 1].created_at));
                    return (
                      <React.Fragment key={msg.id}>
                        {showDateSep && msg.created_at && (
                          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                            <Paper elevation={0} sx={{ px: 2, py: 0.5, bgcolor: '#e3f2fd', borderRadius: 2, border: '1px solid #bbdefb' }}>
                              <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 600, color: '#1976d2' }}>
                                {formatDate(msg.created_at)}
                              </Typography>
                            </Paper>
                          </Box>
                        )}
                        {/* <MessageItem
                          msg={msg}
                          onConceptClick={handleConceptQuestionClick}
                          onDifficultySelect={handleDifficultySelection}
                        /> */}
                        <CommonChatBubble
                          message={msg}
                          aiAvatar={
                            <Box sx={{
                              width: 32, height: 32, borderRadius: '10px',
                              bgcolor: 'primary.main', display: 'flex',
                              alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                            }}>
                              <SmartToyIcon sx={{ color: '#fff', fontSize: 16 }} />
                            </Box>
                          }
                          renderAiContent={(c) => <MathRenderer content={c} />}
                          diagramSlot={msg.diagram_mermaid
                            ? <MermaidDiagram code={msg.diagram_mermaid} title={msg.diagram_title} />
                            : undefined}
                          videoSlot={msg.video_scenes?.length
                            ? <AnimatedVideoPlayer scenes={msg.video_scenes!} title={msg.video_title} />
                            : undefined}
                          imageSlot={
                            <>
                              {getValidImageUrls(msg.image_url).map((url, i) => (
                                <ImagePreview key={`${msg.id}-${i}`} src={url} alt="Content image" title="Image" index={i} />
                              ))}
                            </>
                          }
                          onConceptClick={handleConceptQuestionClick}
                          onDifficultySelect={handleDifficultySelection}
                          formatTime={(d) => formatTime(d, true)}
                          variant="default"
                        />
                      </React.Fragment>
                    );
                  })}
                </>
              )}
              <div ref={messagesEndRef} />
            </Box>

            <FloatingActionBar
              show={(showActionButtons || !isWaitingForResponse) && messages.length > 0}
              awaitingDifficultySelection={awaitingDifficultySelection}
              onNewQuery={handleNewQuery}
              onGetTestPaper={handleGetTestPaper}
              handleGetImage={handleGetImage}
            />

            {/* Input area */}
            <Box sx={{ p: 0, borderTop: '1px solid #e5e7eb', bgcolor: '#fff', flexShrink: 0 }}>
              <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleImageUpload} />

              {imageFiles.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap', px: 1, py: 0.5, bgcolor: '#f0f2f5', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                  {imageFiles.map((file, index) => (
                    <Box key={`${file.name}-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pr: 0.5, borderRadius: 1, bgcolor: '#fff', border: '1px solid #e0e0e0' }}>
                      <img src={imagePreviewUrls[index]} alt="preview" style={{ height: 48, width: 48, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />
                      <Typography variant="caption" sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12, color: '#555' }}>
                        {file.name}
                      </Typography>
                      <Tooltip title="Remove image" arrow>
                        <IconButton size="small" onClick={() => handleRemoveImage(index)}>
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                  <Tooltip title="Clear all images" arrow>
                    <IconButton size="small" onClick={handleClearImage}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
                  </Tooltip>
                </Box>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                  fullWidth multiline maxRows={3}
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onPaste={handlePaste}
                  onKeyPress={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendQuestion(); } }}
                  placeholder={
                    isListening ? 'I am listening...'
                      : isSelectionRequired && (!selectedSubject || !selectedTopic || !selectedSubtopic)
                        ? 'Please select Subject, Topic and Subtopic first...'
                        : 'Type your question here...'
                  }
                  InputProps={{
                    readOnly: isSelectionRequired && (!selectedSubject || !selectedTopic || !selectedSubtopic),
                    inputRef: textFieldRef,
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          <IconButton size="small" onClick={handleMicClick} disabled={isSelectionRequired && (!selectedSubject || !selectedTopic || !selectedSubtopic)}>
                            <MicIcon fontSize="small" />
                          </IconButton>
                          <ScientificKeyboard onInsertSymbol={handleInsertSymbol} selectedSubject={selectedSubject} />
                          <Tooltip title="Attach image" arrow>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={isSelectionRequired && (!selectedSubject || !selectedTopic || !selectedSubtopic)}
                                sx={{ color: imageFiles.length > 0 ? '#667eea' : 'inherit' }}
                              >
                                <AttachFileIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Select mode" arrow>
                            <Button
                              size="small"
                              onClick={(e) => setAnchorEl(e.currentTarget)}
                              endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 12 }} />}
                              sx={{
                                height: 24, minWidth: 'auto', px: 0.8, py: 0, borderRadius: 1.5, textTransform: 'none', fontSize: 11, fontWeight: 500, lineHeight: 1,
                                color: instructionMode !== 'default' ? '#667eea' : '#888',
                                bgcolor: instructionMode !== 'default' ? 'rgba(102,126,234,0.07)' : 'transparent',
                                border: '1px solid', borderColor: instructionMode !== 'default' ? '#667eea' : '#e0e0e0',
                                '&:hover': { bgcolor: 'rgba(102,126,234,0.10)', borderColor: '#667eea', color: '#667eea' },
                                '& .MuiButton-endIcon': { ml: 0.3 },
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                {instructionMode === 'solution' ? <SchoolIcon sx={{ fontSize: 12 }} />
                                  : instructionMode === 'reasoning' ? <LightbulbIcon sx={{ fontSize: 12 }} />
                                    : instructionMode === 'diagram' ? <DescriptionIcon sx={{ fontSize: 12 }} />
                                      : instructionMode === 'video' ? <VideoLibraryIcon sx={{ fontSize: 12 }} />
                                        : <InfoOutlinedIcon sx={{ fontSize: 12 }} />}
                                {instructionMode === 'default' ? 'Mode' : instructionMode === 'solution' ? 'Solution' : instructionMode === 'reasoning' ? 'Reasoning' : instructionMode === 'diagram' ? 'Diagram' : 'Example'}
                              </Box>
                            </Button>
                          </Tooltip>
                        </Box>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleSendQuestion}
                          disabled={!userInput.trim() && imageFiles.length === 0}
                          sx={{ bgcolor: '#667eea', color: '#fff', ml: 0.5, '&:hover': { bgcolor: '#5568d3' }, '&:disabled': { bgcolor: '#e5e7eb', color: '#9e9e9e' } }}
                        >
                          <SendIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3, fontSize: { xs: 13, md: 14 }, paddingRight: '4px' } }}
                />

                <Popover
                  open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}
                  anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  PaperProps={{ sx: { mt: -1, minWidth: isMobile ? 200 : 220, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' } }}
                >
                  <List sx={{ py: 0.5 }} dense>
                    {([
                      { mode: 'default', icon: <InfoOutlinedIcon fontSize="small" />, label: 'Default' },
                      { mode: 'solution', icon: <SchoolIcon fontSize="small" />, label: 'Step by Step Solution' },
                      { mode: 'reasoning', icon: <LightbulbIcon fontSize="small" />, label: 'Step by Step Reasoning' },
                      { mode: 'diagram', icon: <DescriptionIcon fontSize="small" />, label: 'Diagram' },
                      { mode: 'video', icon: <VideoLibraryIcon fontSize="small" />, label: 'Video Lesson' },

                    ] as const).map(({ mode, icon, label }, i, arr) => (
                      <React.Fragment key={mode}>
                        {i > 0 && <Divider sx={{ my: 0.5 }} />}
                        <ListItem
                          button
                          onClick={() => { setInstructionMode(mode); setAnchorEl(null); }}
                          selected={instructionMode === mode}
                          sx={{ py: 0.6, px: 1.5, '&.Mui-selected': { bgcolor: 'rgba(102,126,234,0.08)', '&:hover': { bgcolor: 'rgba(102,126,234,0.12)' } } }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {React.cloneElement(icon, { sx: { color: instructionMode === mode ? '#667eea' : 'inherit' } } as any)}
                          </ListItemIcon>
                          <ListItemText
                            primary={label}
                            primaryTypographyProps={{ fontSize: 14, fontWeight: instructionMode === mode ? 600 : 400, color: instructionMode === mode ? '#667eea' : 'inherit' }}
                          />
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                </Popover>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <HelpDialog open={helpDialogOpen} onClose={() => setHelpDialogOpen(false)} />
      <ResponseNotificationSnackbar
        open={showResponseNotification}
        notificationMessage={notificationMessage}
        onView={handleNavigateToResponse}
        onDismiss={() => { setShowResponseNotification(false); setPendingResponseConversationId(null); }}
      />
    </Container>
  );
};

export default DoubtAssistantPage;
